
//#region Util Imports

import * as _ from "underscore"

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { Api, LocalDictionaryFactory } from './../api/api.module'
import { ColumnModel } from '../api/api.models'

import { LoggerService } from './../common/common.module'

import { CommonHelper, EventsNames, LocalizationHelper, ToastHelper, ConfigHelper, TemplateHelper } from '../../helpers/'

import { Activity, Enums } from '../../models/common/'
import { OpPackage } from "../../models/core"

import { OrderService, OrderCompleteService } from "../../services/order"
import { SystemService } from "../../services/system"
import { UserSessionService } from "../../services/user-session"

import { GridButtonsOptions, GridController, GridCustomButtonsOptions, GridTypes, GridCustomButtonLocation } from "../common/grid"
import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'
import { CreateEditInitParams } from '../common/create-edit-popup/create-edit-popup.controller'

import { SearchBySerialNbrOpitons, ConfirmOrderOpitons, IConfirmOrder, ISearchBySerialNbr, CompleteOrderOptions, ICompleteOrder } from "./components"

export class OrderController extends BaseGridController {

    //#region Costs

    private get ORDERS_GRID_NAME(): string { return 'ORDERS_GRID' }

    //#endregion


    //#region Members

    public completeOrderOptions: CompleteOrderOptions
    public searchOptions: SearchBySerialNbrOpitons
    public confirmOrderOptions: ConfirmOrderOpitons
    public deleteConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }

    private completeOrder: ICompleteOrder
    private completeOrderGridButton: DevExpress.ui.dxButton
    private confirmOrder: IConfirmOrder
    private grid: GridController<OpPackage>
    private searchBySerialNbr: ISearchBySerialNbr

    //#endregion


    //#region Ctor

    constructor(
        protected $scope: ScopeBaseGrid,
        $state: StateService,
        $timeout: ng.ITimeoutService,
        api: Api,
        private logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        private orderService: OrderService,
        private orderCompleteService: OrderCompleteService,
        protected systemService: SystemService,
        protected userSessionService: UserSessionService,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSessionService, systemService, api, localDictionary, null, referenceType)

        this.initializeConfirmations()
        this.initializeEvents()
        this.initializeMainGrid()
        this.initializeCompleteOrder()
        this.initializeSearchByNbr()
    }

    //#endregion


    //#region Events

    private onAdd(): void {
        this.orderService
            .getCreateEditOptions()
            .then((createEditParams: CreateEditInitParams) => {
                this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditParams)
            })
    }

    private onConfirmDelete(): void {
        this.orderService
            .deleteOrder(this.grid.selectedItem)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
            })
            .fail((error: any) => {
                this.logger.log(error)
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.popupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onDeleted(): void {
        this.dataGrid.refresh()
    }

    private onEdit(): void {
        if (this.dataGrid.getSelectedRowKeys().length == 1) {
            this.orderService
                .getCreateEditOptions(this.grid.selectedItemId)
                .then((createEditParams: CreateEditInitParams) => {
                    this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditParams)
                })

        } else {
            ToastHelper.showInfo(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BulkEditIsUnsupported))
        }
    }

    private onInitialized(e: any, grid: GridController<OpPackage>): void {
        this.dataGrid = e.component
        this.grid = grid

        this.setGridId(e.tableId)
    }

    private onShowDetails(): void {
        this.systemService.openDetails({
            moduleName: this.moduleName,
            referenceObjectId: this.grid.selectedItemId,
            pageItemsIds: this.grid.pageItemsIds,
        })
    }

    private onSelectionChanged(): void {
        this.toggleCompleteOrderButton()
    }

    //#endregion


    //#region Complete Order

    private initializeCompleteOrder(): void {
        this.completeOrderOptions = {
            onInitialized: (component: ICompleteOrder) => {
                this.completeOrder = component
            },

            onOrderCompleted: () => {
                this.grid.dxGrid.refresh()
            }
        }
    }

    private openCompleteOrder(): void {
        this.completeOrder.complete(this.orderCompleteService.filterCompletableOrders(this.grid.selectedItems))
    }

    private toggleCompleteOrderButton(): void {
        if (this.completeOrderGridButton != null) {
            this.completeOrderGridButton.option('disabled', !this.areSelectedOrdersCompletable())
        }
    }

    private areSelectedOrdersCompletable(): boolean {
        return _.size(this.orderCompleteService.filterCompletableOrders(this.grid.selectedItems)) > 0
    }

    //#endregion


    //#region Confirm Order

    private initializeConfirmOrder(): void {
        if (this.confirmOrderOptions != null) {
            this.confirmOrder.show()
        } else {
            this.confirmOrderOptions = {
                onInitialized: (e: IConfirmOrder) => {
                    this.confirmOrder = e
                },
                onSaved: () => this.dataGrid.refresh()
            }
        }
    }

    //#endregion


    //#region Search Serial Nbr

    private initializeSearchByNbr(): void {

        this.searchOptions = {
            onFoundPackage: (packageIds: number[]) => {
                this.showPackageDetails(packageIds)
            },

            onInitialized: (e: ISearchBySerialNbr) => {
                this.searchBySerialNbr = e
            },
        }
    }

    private showPackageDetails(packageIds: number[]): void {
        this.systemService.openDetailsOrModule({
            moduleName: Enums.SimaxModuleName.Package,
            referenceObjectIds: packageIds
        })
    }

    private showSearchBySerialNumber(): void {
        if (this.searchOptions != null) {
            this.searchBySerialNbr.show()
        }
    }

    //#endregion


    //#region Setup

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        let items = []

        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_EDIT)) {
            items.push({
                beginGroup: true,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit), ConfigHelper.Icon.EDIT, this.userSessionService),

                onItemClick: (e: any) => this.onEdit(),
            })
        }

        let beginGroup = true

        if (
            this.areSelectedOrdersCompletable()
            // this.userSessionService.getLoggedOperator().hasPermission(Activity.DEPOSITORY_EDIT) //TODO dodać uprawnienie
        ) {
            items.push({
                beginGroup,
                template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CompleteOrder), ConfigHelper.Icon.ORDER, this.userSessionService),

                onItemClick: (e: any) => {
                    this.openCompleteOrder()
                }

            })

            beginGroup = false
        }

        // if (
        //     // this.userSessionService.getLoggedOperator().hasPermission(Activity.DEPOSITORY_EDIT) //TODO dodać uprawnienie
        // ) {
        items.push({
            beginGroup,
            template: TemplateHelper.getContextMenuItem(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfirmOrder), ConfigHelper.Icon.CHECK, this.userSessionService),

            onItemClick: (e: any) => this.initializeConfirmOrder(),
        })
        // }

        return this.mergeWithDefaultItems(isTouchScreen, items)
    }

    private getGridButtonsOptions(): GridButtonsOptions {
        return {
            enabled: true,

            addPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_ADD) ? "['" + Activity[Activity.ORDER_ADD] + "']" : null,
            deletePermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_DEL) ? "['" + Activity[Activity.ORDER_DEL] + "']" : null,
            editPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_EDIT) ? "['" + Activity[Activity.ORDER_EDIT] + "']" : null,
            saveColumnSettingsPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSessionService.getLoggedOperator().hasPermission(Activity.ORDER_LIST) ? "['" + Activity[Activity.ORDER_LIST] + "']" : null,

            onAddClick: () => this.onAdd(),
            onDeleteClick: () => this.onDelete(),
            onEditClick: () => this.onEdit(),
            onShowClick: (e: any) => this.onShowDetails(),
        }
    }

    private getGridCustomButtonOptions(): GridCustomButtonsOptions {
        return {
            buttons: [{
                location: GridCustomButtonLocation.Dropdown,
                options: {
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.ORDER, this.userSession),
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CompleteOrder),

                    onClick: (e: any) => {
                        this.openCompleteOrder()
                    },

                    onInitialized: (e: any) => {
                        this.completeOrderGridButton = e.component
                        this.toggleCompleteOrderButton()
                    }
                }
            }, {
                location: GridCustomButtonLocation.Dropdown,
                options: {
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.CHECK, this.userSession),
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ConfirmOrder),

                    onClick: (e: any) => {
                        this.initializeConfirmOrder()
                    },
                }
            }, {
                location: GridCustomButtonLocation.Dropdown,
                options: {
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.DETAILS, this.userSession),
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.FindPackageBySerialNbr),

                    onClick: (e: any) => {
                        this.showSearchBySerialNumber()
                    },
                }
            }]
        }
    }

    private initializeConfirmations(): void {
        this.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private initializeEvents(): void {
        this.$scope.$on(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CONFIRMED, this.moduleName), (event: ng.IAngularEvent, ...args: any[]) => {
            this.grid.dxGrid.refresh()
        })

        this.$scope.$on(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName), (event: ng.IAngularEvent, ...args: any[]) => {
            this.onDeleted()
        })

        this.$scope.showDetails = (e: any) => {
            this.onShowDetails()
        }
    }

    private initializeMainGrid(): void {
        this.orderService
            .getMainGridColumns()
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    customButtonOptions: this.getGridCustomButtonOptions(),
                    columnsDictionary: columns,
                    defaultColumnsDictionary: defaultColumns,
                    getDataSourceMethod: this.userSessionService.getDataProvider().Order.getOrderFilter,
                    idReferenceType: this.referenceType,
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.orderSaved,
                    name: this.ORDERS_GRID_NAME,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    referenceId: this.referenceType,
                    type: GridTypes.MAIN,

                    allowColumnReordering: !this.userSessionService
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.OrderModuleMainGrid),

                    contextMenuItems: (isTouchScreen: boolean = false) => this.getContextMenuItems(isTouchScreen),
                    onDataReady: (data: any) => this.onDataReady(data),
                    onInitialized: (e: any, grid: GridController<OpPackage>) => this.onInitialized(e, grid),
                    onRowDoubleClick: (e: any) => this.onShowDetails(),
                    onSelectionChanged: (e: any) => this.onSelectionChanged()
                }
            })
    }

    // #endregion
}